import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  TablePaginatedV8,
  TableCellV8,
  TableEditButtons,
  TableDataState,
  promiseModalDialog,
  toastSuccess,
} from 'motion-components';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useUserLocationPermissions } from 'shared-pages/hooks';
import { useGetMaterialsAsOptionsQuery } from 'shared-pages/components';
import MaterialPopover from 'shared-pages/components/popover/popMaterials';

import * as Lo from 'lodash';
import {
  gqlAddProductionLine,
  gqlDeleteProductionLine,
  gqlUpdateProductionLine,
  ProducedMaterialInput,
  ProductionLineInput,
} from '../graphql';
import { useMutation } from '@apollo/client';
import { EditCellV8 } from 'shared-pages/components/edit-cell-new-v8';
import { gridInactiveRowStyle, gridRowSelectedStyle } from 'shared-pages/common/grid-helpfunc';
import { useHistory } from 'react-router-dom';
import { useHandleError } from 'shared-pages/services';
export interface ProductionLine {
  id?: number;
  aa: number;
  productionId: number;
  materialId: number | null;
  netWeight: number;
  producedMaterials?: ProducedMaterialInput[];
  material?: {
    id: number;
    name: string;
  };
}
interface ProductionExtraTableProps {
  productionLines: {
    id: number;
    materialId: number;

    netWeight: number;
    material: {
      id: number;
      name: string;
    };
  }[];
  isNew: boolean;
  productionId: number | null;
  refetch: () => void;
}

const ProductionExtraTable = ({
  productionLines: data,
  isNew,
  productionId,

  refetch,
}: ProductionExtraTableProps) => {
  const history = useHistory();
  const { handleGraphqlError } = useHandleError('productionLine');
  const { t } = useTranslation();
  const { values, setFieldValue, validateForm, resetForm } = useFormikContext<any>();
  const { canAddNew } = useUserLocationPermissions();
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const gridRef = useRef(null);
  const { appStore, tableStore } = useRootStore();
  //mutations
  const [addProductionLine] = useMutation(gqlAddProductionLine);
  const [updateProductionLine] = useMutation(gqlUpdateProductionLine);
  const [deleteProductionLine] = useMutation(gqlDeleteProductionLine);
  // Update production line
  const updateProductionLines = (rowIndex: number, changes: Partial<ProductionLine>) => {
    const currentLines = [...values.productionLines];

    // Validate index (it must be >= 0)
    if (rowIndex < 0) {
      console.warn('Invalid row index:', rowIndex);
      return;
    }
    console.log('ROWINDEX', rowIndex, currentLines.length, 'CHANGES', changes);
    if (rowIndex === currentLines.length) {
      // New row insertion
      const newLine: ProductionLine = {
        // Spread any provided changes
        ...changes,
        // Use sensible defaults:
        aa: changes?.aa ?? rowIndex + 1,
        netWeight: changes.netWeight ?? 0,
        producedMaterials: [], // start with an empty array
        // If material not provided, use default (id: 0, empty name)
        material: changes.material || {
          id: changes.materialId ?? 0,
          name: changes.material.name ?? '',
        },
        // productionId should be set from the parent if available
        productionId: productionId ?? 0,
        materialId: changes.materialId ?? null,
      };
      currentLines.push(newLine);
      console.log('Inserted new row:', newLine);
    } else {
      console.log('Updating existing row at index', rowIndex);
      // Updating an existing row
      const existingLine = currentLines[rowIndex];
      currentLines[rowIndex] = {
        ...existingLine,
        ...changes,
        aa: changes?.aa ?? existingLine?.aa ?? rowIndex + 1,
        netWeight: changes.netWeight ?? existingLine.netWeight ?? 0,
        // Merge the material object if provided, otherwise keep the existing one.
        material: {
          ...(existingLine.material || { id: 0, name: '' }),
          ...(changes.material || {}),
        },
        // Use changes.producedMaterials if provided; otherwise, use the existing one.
        producedMaterials: changes.producedMaterials
          ? Lo.uniqBy(changes.producedMaterials, 'materialId')
          : existingLine.producedMaterials || [],
      };
      console.log('Updated row at index', rowIndex, currentLines[rowIndex]);
    }

    // Update Formik state
    setFieldValue('productionLines', currentLines);
    console.log('Formik state updated:', currentLines);
  };

  const handleProducedMaterialsUpdate = (row: any, newMaterials: ProducedMaterialInput[]) => {
    const index = row.index;
    if (typeof index !== 'number' || index < 0) {
      console.warn('Invalid row index in produced materials update', row);
      return;
    }

    const dedupedMaterials = Lo.uniqBy(newMaterials, 'materialId');
    const updatedLines = [...values.productionLines];

    const existing = updatedLines[index] || {};

    // Fallback to what's in the row itself
    const fallbackMaterialId = row.getValue('materialId') ?? existing.materialId ?? null;
    const fallbackNetWeight = row.getValue('netWeight') ?? existing.netWeight ?? 0;

    updatedLines[index] = {
      ...existing,
      materialId: fallbackMaterialId,
      netWeight: fallbackNetWeight,
      material: existing.material || {
        id: fallbackMaterialId,
        name: row?.original?.material?.name ?? '',
      },
      producedMaterials: dedupedMaterials,
      productionId: productionId ?? 0,
      aa: existing.aa ?? index + 1,
    };

    console.log('→ Updated row in Formik:', updatedLines);

    setFieldValue('productionLines', updatedLines);
  };

  const columns = useMemo(
    () => [
      {
        id: 'aa',
        header: t('Αα'),
        accessorKey: 'aa',
        cell: TableCellV8,
        meta: { type: 'number' },
        size: 10,
      },
      {
        id: 'materialId',
        header: t('Απόβλητο'),
        accessorKey: 'materialId',
        cell: TableCellV8,
        size: 35,
        meta: {
          type: 'select',
          idKey: 'materialId',
          idName: 'material.name',
          hook: useGetMaterialsAsOptionsQuery,
          onValueChange: (value, row) => {
            const materialName = row?.valueName ?? '';
            console.log('MATERIAL_NAME', materialName); // make sure valueName is passed correctly
            updateProductionLines(row.index, {
              materialId: value,
              material: { id: value, name: materialName },
              productionId,
            });
          },
        },
      },
      {
        id: 'netWeight',
        header: t('Καθαρό βάρος'),
        accessorKey: 'netWeight',
        cell: TableCellV8,
        meta: { type: 'number' },
        size: 10,
      },
      {
        id: 'producedMaterials',
        header: t('Παραγόμενα υλικά'),
        accessorKey: 'producedMaterials',
        size: 20,
        cell: (props: any) => {
          const isRowEditing = !!props.table.options.meta?.editedRows?.[props.row.id];
          const canEditPopover = isRowEditing || isNew;

          // Try to locate the row in Formik state based on ID or fallback on aa
          const rowId = props.row.original?.id;
          const rowAa = props.row.original?.aa;

          const line = props.row.original;

          const materialId = props.row.getValue('materialId') || line?.materialId;
          const netWeight = props.row.getValue('netWeight') || line?.netWeight;

          if (!materialId || !netWeight) return null;

          return (
            <>
              <div className="mb-2">
                {line?.producedMaterials.length > 0 &&
                  line?.producedMaterials.map((pm, pi) => (
                    <div
                      key={pi}
                      className="mb-1"
                      style={{ marginLeft: '15px', fontStyle: 'italic' }}
                    >
                      - {pm.material.name}: {pm.netWeight} kg
                    </div>
                  ))}
              </div>
              <MaterialPopover
                key={
                  line?.producedMaterials?.map((pm) => pm.id || pm.materialId).join('-') || 'empty'
                }
                line={{
                  id: line?.id ?? null,
                  materialId,
                  netWeight,
                  material: {
                    id: materialId,
                    name: props.row.original?.material?.name ?? '',
                  },
                  producedMaterials: line?.producedMaterials || [],
                }}
                placement="auto"
                canEdit={canEditPopover}
                onUpdateProducedMaterials={(newMaterials) => {
                  handleProducedMaterialsUpdate(props.row, newMaterials);
                }}
              />
            </>
          );
        },
      },
      {
        id: 'edit',
        header: t('Ενέργειες'),
        accessorKey: 'edit',
        size: 20,
        cell: ({ row, table }) => EditCellV8({ row, table }),
      },
    ],
    [t, values.productionLines],
  );
  const handleDeleteRow = async (row: any) => {
    // row is the ProductionLine object
    if (!row) return false;
    console.log('Deleting row:', row);
    // If row has no ID => it's unsaved. Remove from Formik only.
    if (!row.id) {
      const lines = [...values.productionLines];
      const idx = lines.findIndex((l) => l.aa === row.aa && !l.id);
      if (idx > -1) {
        lines.splice(idx, 1);
        setFieldValue('productionLines', lines);
        console.log('Deleted unsaved production line:', row);
      }
      return true;
    }

    // Otherwise, confirm + delete from server
    const confirm = await promiseModalDialog({
      title: t('Διαγραφή Εγγραφής'),
      text: `Θέλετε να διαγράψετε τη γραμμή παραγωγής για το υλικό "${row.material?.name}" ;`,
    });
    if (!confirm) return false;

    try {
      // actual server call
      await deleteProductionLine({
        variables: { id: row.id },
      });
      toastSuccess(t('Η γραμμή παραγωγής διαγράφηκε με επιτυχία'));
    } catch (err) {
      console.error('Error deleting row:', err);
      return false;
    }

    // Remove from Formik
    const lines = [...values.productionLines];
    const idx = lines.findIndex((l) => l.id === row.id);
    if (idx > -1) {
      lines.splice(idx, 1);
      setFieldValue('productionLines', lines);
    }

    return true;
  };
  const onSaveRow = async (rowIndex: number, row: any, changes: any) => {
    setLoading(true);
    console.log('hitting onSaveRow', rowIndex, row, changes);
    // if there is no materialId,throw error
    if (!changes.materialId && !row.materialId) {
      handleGraphqlError('No materialId registered', 'Δεν υπάρχει επιλεγμένο απόβλητο');
      setLoading(false);
      return false;
    }

    try {
      const formikValues = values;
      const rowValues = { ...row, ...changes };
      console.log('FORMIKVALUES', formikValues);
      const mutValues = Lo.pick(rowValues, Lo.keys(new ProductionLineInput()));
      mutValues.netWeight = parseFloat(rowValues.netWeight);
      mutValues.aa = rowValues.aa ? parseInt(rowValues.aa) : rowIndex + 1;
      let savedId = row?.id;

      // Normalize producedMaterials
      if (row?.producedMaterials) {
        mutValues.producedMaterials = Lo.uniqBy(
          row.producedMaterials.map((pm) => {
            const clean = Lo.pick(pm, [
              'id',
              'materialId',
              'productionLineId',
              'netWeight',
              'percentage',
            ]);
            return {
              ...clean,
              netWeight: parseFloat(clean.netWeight),
              percentage: parseFloat(clean.percentage),
            };
          }),
          'materialId',
        );
      }

      const isNewLine = row.id === '*' || !row.id;
      const isNewProduction = productionId === -1;

      // ✅ CASE 1: Form is new (unsaved production)
      if (isNewProduction) {
        const currentRows = [...formikValues.productionLines];

        const latestLine = currentRows[rowIndex] || {}; // ✅ Get fresh row from Formik

        const newRow = {
          materialId: mutValues.materialId ?? null,
          netWeight: mutValues.netWeight,
          aa: mutValues.aa,
          productionId: productionId ?? 0,
          producedMaterials: mutValues.producedMaterials || [],
          material: {
            id: latestLine.material?.id ?? mutValues.materialId ?? 0,
            name: latestLine.material?.name ?? row.original?.material?.name ?? '', // ✅ Right here
          },
        };

        currentRows[rowIndex] = newRow;
        console.log('✅ Inserted new row in new production (from Formik):', newRow);

        setFieldValue('productionLines', currentRows);
        return true;
      }

      // ✅ CASE 2: Production exists & this is a NEW row (no ID yet)
      if (isNewLine && productionId !== -1) {
        console.log('Adding new row with productionId:', productionId);
        mutValues.productionId = productionId;
        delete mutValues.id; // Remove ID for new insert
        console.log('MUTVALUES', mutValues);

        const res = await addProductionLine({ variables: { data: mutValues } }).catch((err) => {
          console.error('Insert error:', err);
          return null;
        });

        if (!res || !res.data?.addProductionLine) return false;

        savedId = res.data.addProductionLine.id;

        const updatedRow = {
          ...mutValues,
          id: savedId,

          producedMaterials: mutValues.producedMaterials || [],
          material: mutValues.material || { id: mutValues.materialId, name: '' },
        };

        const updatedLines = [...formikValues.productionLines];

        // Replace the placeholder row or push to end
        const idx = updatedLines.findIndex((l) => l.aa === rowValues.aa || l.id === row.id);
        if (idx !== -1) updatedLines[idx] = updatedRow;
        else updatedLines.push(updatedRow);

        setFieldValue('productionLines', updatedLines);
        console.log('Updated lines after insert:', updatedLines);
        await resetForm({ values: { ...formikValues, productionLines: updatedLines } });
        await validateForm();

        return savedId;
      }

      // ✅ CASE 3: Editing existing row
      if (!isNewLine && !isNewProduction) {
        console.log('Updating existing row with productionId:', productionId, mutValues);

        // Get the clean, up-to-date line from Formik
        const updatedLineInFormik = formikValues.productionLines.find((l) => l.id === row.id);

        // ✅ Always sanitize producedMaterials (even empty arrays)
        const cleanedMaterials = Array.isArray(updatedLineInFormik?.producedMaterials)
          ? updatedLineInFormik.producedMaterials.map((pm) => {
              const clean = Lo.pick(pm, [
                'id',
                'materialId',
                'productionLineId',
                'netWeight',
                'percentage',
              ]);
              return {
                ...clean,
                netWeight: parseFloat(clean.netWeight),
                percentage: parseFloat(clean.percentage),
              };
            })
          : [];

        mutValues.producedMaterials = Lo.uniqBy(cleanedMaterials, 'materialId');

        console.log('updateProductionLine', mutValues);

        const res = await updateProductionLine({ variables: { data: mutValues } }).catch((err) => {
          console.error('Update error:', err);
          return null;
        });

        if (!res || !res.data?.updateProductionLine) return false;

        const updatedRow = res?.data?.updateProductionLine;

        const updatedLines = [...formikValues.productionLines];

        // ✅ Try to find row by ID, or fallback to index
        let idx = updatedLines.findIndex((l) => l.id === row.id);
        if (idx === -1) {
          console.warn('⚠️ Could not find row by ID. Falling back to index.');
          idx = rowIndex;
        }

        if (idx !== -1) {
          updatedLines[idx] = updatedRow;
          setFieldValue('productionLines', updatedLines);
          console.log('✅ Successfully updated row at index:', idx);
        } else {
          console.error('❌ Could not update row — index not found:', row);
          return false;
        }
        await resetForm({ values: { ...formikValues, productionLines: updatedLines } });
        await validateForm();
        return savedId;
      }

      return false;
    } catch (err) {
      console.error('onSaveRow error:', err);
      return false;
    } finally {
      setLoading(false);
      // Reset the form to clear any errors or unsaved changes
      refetch(); // Refetch the data to ensure it's up-to-date
      // Reload the page to reflect changes
    }
  };

  return (
    <LoadingOverlay active={loading} spinner text={t('Please wait...')}>
      <div className="form-group-title d-flex justify-content-between align-items-center">
        <h5 className="mb-0">{t('Προσθετά Στοιχεία Παραγωγής')}</h5>
        <TableEditButtons
          className="me-3"
          canEdit={true}
          canInsert={canAddNew}
          canDelete={true}
          tableOptionsMeta={() =>
            gridRef.current ? (gridRef.current as any).table.options.meta : undefined
          }
          twoLines={false}
        />
      </div>

      {values?.productionLines ? (
        <TablePaginatedV8
          style={{ overflow: 'visible', minWidth: '100%' }}
          ref={gridRef}
          loading={false}
          id="production-extra-table"
          appStore={appStore}
          onBeforeEditRow={async () => true}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          onSaveRow={onSaveRow}
          deleteRow={handleDeleteRow}
          onNewRow={() => ({
            id: '*',
            isActive: true,
            aa: values.productionLines.length + 1,
            materialId: null,
            material: {
              id: null,
              name: '',
            },
            producedMaterials: [],

            netWeight: 0,
          })}
          data={values.productionLines}
          onRowClick={(props) => {
            // props.row.original is the actual row object
            setSelected(props.row.original);
          }}
          rowProps={(row: any) => {
            let style = undefined;
            // highlight selected row
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            // if row is inactive (just an example)
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            // if row has errors
            if (row.getIsSelected()) style = { ...style, backgroundColor: '#f8d7da' };
            return { style };
          }}
          dataCount={values.productionLines.length}
          topBorderRadius={false}
          initSorting={[{ id: 'material.name', desc: true }]}
        />
      ) : (
        <div style={{ height: '200px' }} />
      )}
    </LoadingOverlay>
  );
};

export default ProductionExtraTable;
